import React, { useCallback, useEffect, useState } from 'react';
import { IDSButton, IDSInput, IDSAccordion, IDSCheckButton, IDSTextarea, IDSCheckbox } from '@inera/ids-react';
import ConfirmDialog from '../ConfirmDialog';
import StatusAlert from '../StatusAlert';
import { fetchAllUsers } from '../../services/usersAdminAPI';
import {
  fetchDepartments,
  fetchDepartmentMembers,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  addDepartmentMember,
  removeDepartmentMember,
} from '../../services/departmentsAdminAPI';
import { REVIEWING_ORGANISATION, belongsToReviewingOrg } from '../../utils/reviewingOrganisation';
import { fetchAdminForms, setFormDepartments } from '../../services/formsAdminAPI';

const userLabel = (user) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name || user.username || user.email || user.keycloakUserId;
};

// ── Department form (create/edit) ─────────────────────────────────────────────
const DepartmentForm = ({ initial = {}, onSave, onCancel, submitLabel = 'Spara' }) => {
  const [code, setCode] = useState(initial.code || '');
  const [name, setName] = useState(initial.name || '');
  const [description, setDescription] = useState(initial.description || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!code.trim()) errors.code = 'Kod krävs.';
    else if (code.trim().length > 50) errors.code = 'Koden får vara högst 50 tecken.';
    if (!name.trim()) errors.name = 'Namn krävs.';

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    setError(null);
    try {
      await onSave({
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || null,
      });
    } catch (err) {
      setError(err.message || 'Något gick fel. Försök igen.');
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      style={{
        background: 'var(--ids-palette-neutral-95)',
        border: '1px solid var(--ids-palette-neutral-90)',
        borderRadius: '6px',
        padding: '1.25rem',
        marginBottom: '1rem',
      }}
    >
      <div className="ids-mb-3">
        <IDSInput
          label="Kod *"
          type="text"
          value={code}
          invalid={!!fieldErrors.code}
          errorMsg={fieldErrors.code}
          onChange={(e) => setCode(e.target.value)}
        />
        <p style={{ fontSize: '0.8rem', color: 'var(--ids-palette-neutral-50)', marginTop: '0.25rem' }}>
          Kort unik identifierare, t.ex. "ehalsa".
        </p>
      </div>
      <div className="ids-mb-3">
        <IDSInput
          label="Namn *"
          type="text"
          value={name}
          invalid={!!fieldErrors.name}
          errorMsg={fieldErrors.name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="ids-mb-3">
        <IDSTextarea
          label="Beskrivning"
          block
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <StatusAlert error={error} className="ids-mb-3" />

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <IDSButton size="s" type="submit" disabled={loading}>
          {loading ? 'Sparar...' : submitLabel}
        </IDSButton>
        <IDSButton size="s" secondary type="button" onClick={onCancel}>
          Avbryt
        </IDSButton>
      </div>
    </form>
  );
};

// ── Member assignment for one department ──────────────────────────────────────
const MemberAssignments = ({ department, candidates, onMemberCountChange }) => {
  const [memberIds, setMemberIds] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchDepartmentMembers(department.departmentId)
      .then((members) => {
        if (!cancelled) setMemberIds(new Set(members.map((m) => m.userId)));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Kunde inte hämta medlemmar.');
      });

    return () => {
      cancelled = true;
    };
  }, [department.departmentId]);

  const toggle = async (user) => {
    if (!memberIds) return;

    const userId = user.keycloakUserId;
    const isMember = memberIds.has(userId);

    setBusy(userId);
    setError(null);
    try {
      if (isMember) {
        await removeDepartmentMember(department.departmentId, userId);
      } else {
        await addDepartmentMember(department.departmentId, userId, {
          name: userLabel(user),
          email: user.email,
        });
      }

      setMemberIds((prev) => {
        const next = new Set(prev);
        if (isMember) next.delete(userId);
        else next.add(userId);
        onMemberCountChange(department.departmentId, next.size);
        return next;
      });
    } catch (err) {
      setError(err.message || 'Något gick fel. Försök igen.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      style={{
        marginTop: '1.25rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--ids-palette-neutral-90)',
      }}
    >
      <p style={{ fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.85rem', color: 'var(--ids-palette-neutral-30)' }}>
        Medlemmar:
      </p>

      {memberIds === null && !error && <p style={{ fontSize: '0.9rem' }}>Laddar medlemmar...</p>}

      {memberIds !== null && candidates.length === 0 && (
        <p style={{ color: 'var(--ids-palette-neutral-70)', fontSize: '0.9rem' }}>
          Inga användare i {REVIEWING_ORGANISATION} hittades.
        </p>
      )}

      {memberIds !== null && candidates.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {candidates.map((user) => {
            const assigned = memberIds.has(user.keycloakUserId);
            return (
              <IDSCheckButton
                key={`${user.keycloakUserId}-${assigned}`}
                defaultChecked={assigned}
                disabled={busy === user.keycloakUserId}
                onChange={() => toggle(user)}
              >
                {userLabel(user)}
              </IDSCheckButton>
            );
          })}
        </div>
      )}

      <StatusAlert error={error} className="ids-mt-3" />
    </div>
  );
};

// ── Department accordion ──────────────────────────────────────────────────────
const DepartmentAccordion = ({ department, candidates, onUpdate, onDelete, onMemberCountChange }) => {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async (payload) => {
    const updated = await updateDepartment(department.departmentId, payload);
    onUpdate(updated);
    setEditing(false);
  };

  const toggleActive = async () => {
    setError(null);
    try {
      const updated = await updateDepartment(department.departmentId, { active: !department.active });
      onUpdate(updated);
    } catch (err) {
      setError(err.message || 'Något gick fel. Försök igen.');
    }
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    setError(null);
    try {
      await deleteDepartment(department.departmentId);
      onDelete(department.departmentId);
    } catch (err) {
      setError(err.message || 'Något gick fel. Försök igen.');
      setDeleting(false);
    }
  };

  return (
    <>
      {confirmDelete && (
        <ConfirmDialog
          message={`Vill du ta bort avdelningen "${department.name}"? Formulär kopplade till avdelningen blir synliga för alla granskare.`}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      <IDSAccordion
        headline={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3>{department.name}</h3>
            <span style={{ fontSize: '0.85rem', color: 'var(--ids-palette-neutral-60)' }}>
              {department.code} · {department.memberCount} medlemmar
              {department.active ? '' : ' · inaktiv'}
            </span>
          </div>
        }
        headlineSize="m"
        level={1}
      >
        <div>
          {department.description && (
            <p className="ids-mb-4" style={{ color: 'var(--ids-palette-neutral-50)', fontSize: '0.9rem' }}>
              {department.description}
            </p>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <IDSButton size="s" type="button" onClick={() => setEditing((v) => !v)}>
              {editing ? 'Avbryt redigering' : 'Redigera avdelning'}
            </IDSButton>
            <IDSButton size="s" secondary type="button" onClick={toggleActive}>
              {department.active ? 'Inaktivera' : 'Aktivera'}
            </IDSButton>
            <IDSButton size="s" type="button" disabled={deleting} onClick={() => setConfirmDelete(true)}>
              {deleting ? '...' : 'Ta bort avdelning'}
            </IDSButton>
          </div>

          <StatusAlert error={error} className="ids-mb-3" />

          {editing && (
            <DepartmentForm
              initial={department}
              onSave={handleSave}
              onCancel={() => setEditing(false)}
            />
          )}

          <MemberAssignments
            department={department}
            candidates={candidates}
            onMemberCountChange={onMemberCountChange}
          />
        </div>
      </IDSAccordion>
    </>
  );
};

// ── Form ownership ────────────────────────────────────────────────────────────
// A form may belong to any number of departments. Reviewers in a department see
// submissions for that department's forms plus every form without departments.
const FormOwnership = ({ departments }) => {
  const [forms, setForms] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);
  // Bumped when a save fails so the uncontrolled check buttons remount and drop
  // the toggle the server never accepted.
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let cancelled = false;

    Promise.resolve(fetchAdminForms())
      .then((data) => {
        if (!cancelled) setForms(data || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setForms([]);
          setError(err.message || 'Kunde inte hämta formulär.');
        }
      });

    return () => { cancelled = true; };
  }, []);

  // The whole ownership list is sent on every toggle, so the server state always
  // mirrors exactly what is checked here.
  const toggleDepartment = async (form, departmentId) => {
    const current = form.departmentIds || [];
    const next = current.includes(departmentId)
      ? current.filter((id) => id !== departmentId)
      : [...current, departmentId];

    setBusy(form.formId);
    setError(null);
    setSaved(null);
    try {
      const updated = await setFormDepartments(form.formId, next);
      setForms((prev) => prev.map((f) => (f.formId === form.formId ? { ...f, ...updated } : f)));
      setSaved(form.formId);
    } catch (err) {
      setError(err.message || 'Kunde inte spara avdelningar.');
      setRevision((v) => v + 1);
    } finally {
      setBusy(null);
    }
  };

  const clearDepartments = async (form) => {
    if (!(form.departmentIds || []).length) return;

    setBusy(form.formId);
    setError(null);
    setSaved(null);
    try {
      const updated = await setFormDepartments(form.formId, []);
      setForms((prev) => prev.map((f) => (f.formId === form.formId ? { ...f, ...updated } : f)));
      setSaved(form.formId);
    } catch (err) {
      setError(err.message || 'Kunde inte spara avdelningar.');
      setRevision((v) => v + 1);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="ids-mt-6">
      <h2 className="ids-heading-m ids-mb-2">Formulärens ägarskap</h2>
      <p className="ids-body ids-mb-4" style={{ color: 'var(--ids-palette-neutral-30)' }}>
        Välj vilka avdelningar som äger varje E2E-formulär. Ett formulär kan ägas av flera
        avdelningar samtidigt. Standardvärdet är <strong>Ingen</strong>, vilket innebär att
        formuläret syns för alla granskare oavsett avdelning.
      </p>

      <StatusAlert error={error} className="ids-mb-3" />

      {forms === null && <p>Laddar formulär...</p>}

      {forms !== null && forms.length === 0 && !error && (
        <p style={{ color: 'var(--ids-palette-neutral-60)' }}>Inga formulär hittades.</p>
      )}

      {forms !== null && forms.map((form) => {
        const selected = form.departmentIds || [];

        return (
          <div
            key={form.formId}
            style={{
              padding: '0.75rem 0',
              borderBottom: '1px solid var(--ids-palette-neutral-90)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: '1rem',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ minWidth: '14rem', flex: '1 1 16rem' }}>
                <p style={{ fontWeight: 600, margin: 0 }}>{form.formTitle}</p>
                <span style={{ fontSize: '0.85rem', color: 'var(--ids-palette-neutral-60)' }}>
                  Version {form.version || '–'}
                </span>
              </div>

              <span style={{ fontSize: '0.85rem' }}>
                {busy === form.formId && 'Sparar...'}
                {busy !== form.formId && saved === form.formId && (
                  <span style={{ color: 'var(--ids-palette-status-success-40)' }}>Sparat</span>
                )}
              </span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>
              <IDSCheckButton
                key={`${form.formId}-none-${selected.length === 0}-${revision}`}
                defaultChecked={selected.length === 0}
                disabled={busy === form.formId || selected.length === 0}
                onChange={() => clearDepartments(form)}
              >
                Ingen
              </IDSCheckButton>

              {departments.map((department) => {
                const assigned = selected.includes(department.departmentId);
                return (
                  <IDSCheckButton
                    key={`${form.formId}-${department.departmentId}-${assigned}-${revision}`}
                    defaultChecked={assigned}
                    disabled={busy === form.formId}
                    onChange={() => toggleDepartment(form, department.departmentId)}
                  >
                    {department.name}
                    {department.active ? '' : ' (inaktiv)'}
                  </IDSCheckButton>
                );
              })}
            </div>

            {departments.length === 0 && (
              <p style={{ fontSize: '0.85rem', color: 'var(--ids-palette-neutral-60)', margin: '0.25rem 0 0' }}>
                Skapa en avdelning först för att kunna tilldela ägarskap.
              </p>
            )}
          </div>
        );
      })}
    </section>
  );
};

// ── Main layout ───────────────────────────────────────────────────────────────
const DepartmentsAdminLayout = () => {
  const [departments, setDepartments] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showInactive, setShowInactive] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [departmentsData, usersData] = await Promise.all([fetchDepartments(), fetchAllUsers()]);
        setDepartments(departmentsData);
        setCandidates(
          usersData
            .filter(belongsToReviewingOrg)
            .sort((a, b) => userLabel(a).localeCompare(userLabel(b), 'sv'))
        );
      } catch (err) {
        setError(err.message || 'Något gick fel. Försök igen.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCreate = async (payload) => {
    const created = await createDepartment(payload);
    setDepartments((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'sv')));
    setCreating(false);
  };

  const handleUpdate = (updated) => {
    setDepartments((prev) =>
      prev.map((d) => (d.departmentId === updated.departmentId ? updated : d))
    );
  };

  const handleDelete = (departmentId) => {
    setDepartments((prev) => prev.filter((d) => d.departmentId !== departmentId));
  };

  const handleMemberCountChange = useCallback((departmentId, memberCount) => {
    setDepartments((prev) =>
      prev.map((d) => (d.departmentId === departmentId ? { ...d, memberCount } : d))
    );
  }, []);

  const visibleDepartments = showInactive ? departments : departments.filter((d) => d.active);

  return (
    <main className="ids-content ids-p-6" style={{ maxWidth: '860px', margin: '2rem auto' }}>
      <h1 className="ids-heading-l ids-mb-2">Avdelningar</h1>
      <p className="ids-body ids-mb-4" style={{ color: 'var(--ids-palette-neutral-30)' }}>
        En avdelning äger E2E-formulär. Granskare i en avdelning ser endast inlämningar för
        avdelningens formulär. Formulär utan avdelning är synliga för alla granskare.
      </p>

      <section className="ids-mt-6">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="ids-heading-m">Alla avdelningar</h2>
          <IDSButton size="s" type="button" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Avbryt' : '+ Skapa ny avdelning'}
          </IDSButton>
        </div>

        {creating && (
          <DepartmentForm
            onSave={handleCreate}
            onCancel={() => setCreating(false)}
            submitLabel="Skapa"
          />
        )}

        {!loading && !error && departments.length > 0 && (
          <div className="ids-mb-4">
            <IDSCheckbox checked={showInactive} onChange={() => setShowInactive((v) => !v)}>
              Visa inaktiva avdelningar
            </IDSCheckbox>
          </div>
        )}

        {loading && <p>Laddar...</p>}
        <StatusAlert error={error} />
        {!loading && !error && departments.length === 0 && (
          <p style={{ color: 'var(--ids-palette-neutral-60)' }}>Inga avdelningar hittades.</p>
        )}

        {visibleDepartments.map((department) => (
          <DepartmentAccordion
            key={department.departmentId}
            department={department}
            candidates={candidates}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onMemberCountChange={handleMemberCountChange}
          />
        ))}
      </section>

      {!loading && !error && <FormOwnership departments={departments} />}
    </main>
  );
};

export default DepartmentsAdminLayout;
